/**
 * DXF writer version model (docs/opencadstudio-parity/01-native-dxf-export.md
 * §2.1, §3.1). The writer always emits handles + owner + subclass markers
 * (OCS parity, issue #68: reporting R12 while writing R13+ constructs would
 * be a lie), so the lowest supported target is R14 (AC1014) — R12/AC1009 is
 * always upgraded to at least AC1014 semantics.
 */

import type { Layer } from '../../../state/store';

export type DxfVersion =
    | 'AC1014' // R14
    | 'AC1015' // 2000
    | 'AC1018' // 2004
    | 'AC1021' // 2007
    | 'AC1024' // 2010
    | 'AC1027' // 2013
    | 'AC1032'; // 2018

export interface DxfVersionInfo {
    version: DxfVersion;
    /** Human release name (dialog label uses it). */
    release: string;
    /** True color groups 420/430/440 are written — AC1018+ (below: ACI 62). */
    trueColor: boolean;
    /** ATTRIB version byte (280) is written — AC1024+. */
    attribVersionByte: boolean;
    /** INSERT lock-position byte (280) is written — AC1021+. */
    insertLockByte: boolean;
    /** MTEXT R2018 embedded-object column block (101) — AC1032 only. */
    mText2018Columns: boolean;
    /** ACIS SAB (AcDs section) for model documentation — AC1027+. */
    acdsSab: boolean;
}

const makeInfo = (
    version: DxfVersion,
    release: string,
    gates: { trueColor?: boolean; attribVersionByte?: boolean; insertLockByte?: boolean; mText2018Columns?: boolean; acdsSab?: boolean } = {}
): DxfVersionInfo => ({
    version,
    release,
    trueColor: !!gates.trueColor,
    attribVersionByte: !!gates.attribVersionByte,
    insertLockByte: !!gates.insertLockByte,
    mText2018Columns: !!gates.mText2018Columns,
    acdsSab: !!gates.acdsSab,
});

/** Version gate table, ordered oldest → newest (R14 … 2018). */
export const DXF_VERSION_INFO: Record<DxfVersion, DxfVersionInfo> = {
    AC1014: makeInfo('AC1014', 'R14'),
    AC1015: makeInfo('AC1015', '2000'),
    AC1018: makeInfo('AC1018', '2004', { trueColor: true }),
    AC1021: makeInfo('AC1021', '2007', { trueColor: true, insertLockByte: true }),
    AC1024: makeInfo('AC1024', '2010', { trueColor: true, insertLockByte: true, attribVersionByte: true }),
    AC1027: makeInfo('AC1027', '2013', { trueColor: true, insertLockByte: true, attribVersionByte: true, acdsSab: true }),
    AC1032: makeInfo('AC1032', '2018', { trueColor: true, insertLockByte: true, attribVersionByte: true, mText2018Columns: true, acdsSab: true }),
};

/** Dialog order (newest first) — mirrors the OCS SAVEAS version list. */
export const DXF_VERSION_CHOICES: DxfVersionInfo[] = [
    DXF_VERSION_INFO.AC1032,
    DXF_VERSION_INFO.AC1024,
    DXF_VERSION_INFO.AC1021,
    DXF_VERSION_INFO.AC1018,
    DXF_VERSION_INFO.AC1015,
    DXF_VERSION_INFO.AC1014,
];

export const DEFAULT_DXF_VERSION: DxfVersion = 'AC1032';

/** Resolve a raw version string ('AC1032', '2018', 'R14'…) to its info. */
export function resolveDxfVersion(raw: string | undefined): DxfVersionInfo {
    const key = String(raw ?? '').trim().toUpperCase();
    if (key in DXF_VERSION_INFO) return DXF_VERSION_INFO[key as DxfVersion];
    if (key === '2018' || key === 'R2018') return DXF_VERSION_INFO.AC1032;
    if (key === '2010' || key === 'R2010') return DXF_VERSION_INFO.AC1024;
    if (key === '2007' || key === 'R2007') return DXF_VERSION_INFO.AC1021;
    if (key === '2004' || key === 'R2004') return DXF_VERSION_INFO.AC1018;
    if (key === '2000' || key === 'R2000') return DXF_VERSION_INFO.AC1015;
    if (key === 'R14' || key === '14') return DXF_VERSION_INFO.AC1014;
    return DXF_VERSION_INFO[DEFAULT_DXF_VERSION];
}

// ---------------------------------------------------------------------------
// Write context shared by every section/serializer
// ---------------------------------------------------------------------------

/** One linetype table record: dash pattern in drawing units (49 codes). */
export interface LinetypeDef {
    name: string;
    description: string;
    /** Dash list (positive = draw, negative = gap); empty = continuous. */
    dashes: number[];
}

export interface TextStyleTableEntry {
    handle: string;
    /** DXF record name (STANDARD or the yap text style name). */
    name: string;
    /** Fixed text height (40); 0 = not fixed. */
    height: number;
    /** Primary font file hint (3). */
    fontFile: string;
    /** Big font (4) — always empty in Faz 1. */
    bigFont: string;
    /** yap alignment (drives the MTEXT attachment point 71). */
    alignment: 'left' | 'center' | 'right';
}

export interface DimStyleTableEntry {
    handle: string;
    /** DXF record name (the yap dimension style name). */
    name: string;
    /** DIMSCALE (40). */
    scale: number;
    /** DIMASZ (41) — arrow size. */
    arrowSize: number;
    /** DIMEXO (42) — extension line offset. */
    extensionOffset: number;
    /** DIMEXE (44) — extension overshoot. */
    extensionOvershoot: number;
    /** DIMTXT (140) — text height. */
    textHeight: number;
    /** DIMGAP (147) — text gap. */
    textGap: number;
    /** DIMDEC (271) — decimal precision. */
    precision: number;
    /** DIMPOST (3) — suffix text. */
    suffix: string;
    /** DIMCLRD (176) — dimension line color ACI. */
    lineColorAci: number;
    /** DIMCLRT (179) — text color ACI. */
    textColorAci: number;
}

export interface LayerTableEntry {
    handle: string;
    layer: Layer;
    /** Resolved ACI (62 sign carries the on/off state). */
    aci: number;
}

export interface DxfWriteContext {
    version: DxfVersionInfo;
    handles: import('./handleAllocator').HandleAllocator;
    /** Layer id → resolved table entry (handles + ACI). */
    layerEntries: Map<string, LayerTableEntry>;
    /** DXF linetype records by name. */
    ltypeEntries: Map<string, { handle: string; def: LinetypeDef }>;
    /** DXF text style records by record name. */
    styleEntries: Map<string, TextStyleTableEntry>;
    /** yap dim style id → DIMSTYLE record. */
    dimStyleEntries: Map<string, DimStyleTableEntry>;
    /** Block name → BLOCK_RECORD handle (*Model_Space/*Paper_Space/*D… included). */
    blockRecords: Map<string, string>;
    /** *Model_Space block record handle (owner of ENTITIES content). */
    modelSpaceRecord: string;
    /** *Paper_Space block record handle. */
    paperSpaceRecord: string;
    /** 'Normal' plot style record handle (layer group 390). */
    plotStyleNormal: string;
    /** Anonymous dimension blocks baked for this export (name → geometry). */
    dimBlocks: Map<string, import('./dimBlockBake').BakedDimensionBlock>;
    /**
     * TABLE shell handles (LTYPE…BLOCK_RECORD) + the two APPID entry
     * handles — all pre-allocated during context build so $HANDSEED,
     * written first, still covers every handle in the file.
     */
    tableShells: Record<'LTYPE' | 'LAYER' | 'STYLE' | 'APPID' | 'DIMSTYLE' | 'BLOCK_RECORD', string>;
    appIdHandles: [string, string];
}
